import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import * as Analytics from '../services/AnalyticsService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Detect a mobile/touch device — used to pick the fullscreen target so that
// the FS state survives SPA-navigation to the panorama page (mobile only).
const isMobileDevice = () => {
  try { return window.matchMedia('(max-width: 900px), (pointer: coarse)').matches; }
  catch (e) { return false; }
};


const fmt = (s) => {
  if (!s || isNaN(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

const InteractiveGamePlayer = ({ gameId, poster, style, getAuthHeader, coverOnly }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);


  const containerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const lastNavTimeRef = useRef(-1);
  const [showFlash, setShowFlash] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [hlsReady, setHlsReady] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processingInfo, setProcessingInfo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [qualities, setQualities] = useState([]);
  const [currentQuality, setCurrentQuality] = useState(-1);
  const [showQualityMenu, setShowQualityMenu] = useState(false);
  const [timecodes, setTimecodes] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(typeof document !== 'undefined' ? !!document.fullscreenElement : false);
  const savedVol = useRef(parseFloat(sessionStorage.getItem('playerVolume') ?? '0.5'));
  const [volume, setVolume] = useState(savedVol.current);
  const [isMuted, setIsMuted] = useState(sessionStorage.getItem('playerMuted') === 'true');
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const hideTimer = useRef(null);
  const [isSeeking, setIsSeeking] = useState(false);

  // Poll processing status
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const resp = await axios.get(`${API}/games/${gameId}/video/status?t=${Date.now()}`);
        const st = resp.data;
        setProcessing(st.processing);
        setHlsReady(st.hls_ready);
        if (st.processing) {
          setProcessingInfo({
            step: st.processing_step || 'Обработка...',
            progress: st.processing_progress || 0,
            etaSec: st.processing_eta_sec || 0,
            elapsedSec: st.processing_elapsed_sec || 0,
            fileSizeMb: st.video_file_size_mb || 0,
            durationSec: st.video_duration_sec || 0
          });
        } else {
          setProcessingInfo(null);
        }
      } catch (e) {}
    };
    checkStatus();
    const iv = setInterval(checkStatus, processing ? 3000 : 10000);
    return () => clearInterval(iv);
  }, [gameId, processing]);

  // Load timecodes
  useEffect(() => {
    if (!gameId) return;
    const load = async () => {
      try {
        const headers = getAuthHeader ? getAuthHeader() : {};
        const resp = await axios.get(`${API}/games/${gameId}/video/timecodes`, { headers });
        setTimecodes(resp.data || []);
      } catch (e) {
        setTimecodes([]);
      }
    };
    load();
  }, [gameId, getAuthHeader]);

  // Init HLS
  useEffect(() => {
    if (!hlsReady || !videoRef.current) return;
    const video = videoRef.current;
    const hlsUrl = `${API}/games/${gameId}/video/hls/master.m3u8`;

    // Always prefer HLS.js for quality switching control
    if (window.Hls && window.Hls.isSupported()) {
      // Use HLS.js below
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = hlsUrl;
      return;
    } else {
      return;
    }

    const hls = new window.Hls({ maxBufferLength: 30, maxMaxBufferLength: 60 });
    hlsRef.current = hls;
    hls.loadSource(hlsUrl);
    hls.attachMedia(video);

    hls.on(window.Hls.Events.MANIFEST_PARSED, (_, data) => {
      const lvls = hls.levels.map((l, i) => ({ index: i, height: l.height, width: l.width, bitrate: l.bitrate }));
      setQualities(lvls);
      setCurrentQuality(-1); // auto
    });

    hls.on(window.Hls.Events.LEVEL_SWITCHED, (_, data) => {
      setCurrentQuality(data.level);
    });

    hls.on(window.Hls.Events.ERROR, (_, data) => {
      if (data.fatal) {
        if (data.type === window.Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
        else if (data.type === window.Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
        else hls.destroy();
      }
    });

    return () => { hls.destroy(); hlsRef.current = null; };
  }, [hlsReady, gameId]);

  // Video event handlers
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    // Apply saved volume on init
    video.volume = savedVol.current;
    video.muted = sessionStorage.getItem('playerMuted') === 'true';
    // Throttle ref shared across onTime closures
    let lastProgressSent = 0;
    const onTime = () => {
      if (!isSeeking) setCurrentTime(video.currentTime);
      // Feed analytics — keeps lastProgressTime in AnalyticsService up to date so
      // duration_watched / completion_percent are non-zero when the view ends.
      const now = Date.now();
      if (now - lastProgressSent > 2000) {
        try { Analytics.trackVideoProgress(video.currentTime); } catch (_) {}
        lastProgressSent = now;
      }
    };
    const onDur = () => setDuration(video.duration);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onVol = () => {
      setVolume(video.volume);
      setIsMuted(video.muted);
      sessionStorage.setItem('playerVolume', String(video.volume));
      sessionStorage.setItem('playerMuted', String(video.muted));
    };
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('durationchange', onDur);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('volumechange', onVol);
    return () => {
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('durationchange', onDur);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('volumechange', onVol);
    };
  }, [hlsReady, isSeeking]);

  // Timecode navigation - auto-navigate to panorama when timecode is reached
  useEffect(() => {
    if (!timecodes.length || !gameId || !hlsReady) return;

    const checkTimecodes = () => {
      const v = videoRef.current;
      if (!v || v.paused || countdown > 0) return;

      const ct = v.currentTime;
      for (const tc of timecodes) {
        const tTime = tc.timecode || tc.time;
        if (ct >= tTime && ct < tTime + 0.3) {
          if (lastNavTimeRef.current !== tTime) {
            lastNavTimeRef.current = tTime;
            const targetPath = '/games/' + gameId + '/guess/' + tc.panorama_id;

            if (location.pathname !== targetPath) {
              v.pause();
              setShowFlash(true);

              try {
                const audio = new Audio('/game/sounds/shutter.wav');
                audio.volume = 0.7;
                audio.play().catch(() => {});
              } catch(e) {}

              // Save state for mini-player resume. FS is on <html> now, so it
              // persists through SPA navigation — the panorama appears already
              // in fullscreen+landscape with no re-entry rumble.
              const wasFullscreen = !!document.fullscreenElement;
              sessionStorage.setItem('playerWasFullscreen', String(wasFullscreen));
              sessionStorage.setItem('interactivePlayerResumeState', JSON.stringify({
                isActive: true,
                videoUrl: `${API}/games/${gameId}/video/hls/master.m3u8`,
                gameId,
                timecodes,
                currentTime: v.currentTime + 1,
                lastNavTime: tTime,
                needsCountdown: true,
                wasFullscreen
              }));

              // FS stays alive on <html>, so we don't need the 150 ms shutter to
              // mask the exit-FS flash. Navigate ASAP if we're in FS.
              const flashMs = wasFullscreen ? 0 : 150;
              setTimeout(() => {
                setShowFlash(false);
                navigate(targetPath);
              }, flashMs);
              return;
            }
          }
        }
      }
    };

    const iv = setInterval(checkTimecodes, 100);
    return () => clearInterval(iv);
  }, [timecodes, gameId, hlsReady, location.pathname, navigate, countdown]);

  // Countdown after returning from panorama
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  // Resume playback after returning from panorama
  const [pendingResumeTime, setPendingResumeTime] = useState(null);
  const [pendingFullscreen, setPendingFullscreen] = useState(false);

  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('interactivePlayerResumeState');
      if (saved) {
        const state = JSON.parse(saved);
        if (state.gameId === gameId && state.currentTime > 0) {
          setPendingResumeTime(state.currentTime);
          setPendingFullscreen(!!state.wasFullscreen);
          // Restore lastNavTime to prevent immediate re-trigger of same timecode
          if (state.lastNavTime !== undefined) {
            lastNavTimeRef.current = state.lastNavTime;
          }
          sessionStorage.removeItem('interactivePlayerResumeState');
        }
      }
    } catch(e) {}
  }, [gameId]);

  // Apply resume when HLS is ready and video element is available
  useEffect(() => {
    if (pendingResumeTime === null || !hlsReady || !videoRef.current) return;
    const v = videoRef.current;

    const doResume = () => {
      v.currentTime = pendingResumeTime;
      setCurrentTime(pendingResumeTime);
      setPendingResumeTime(null);
      // If video was in fullscreen when the timecode navigation happened
      // but the browser-native FS state did not survive the route change,
      // re-request FS on <html>. This is allowed even without a fresh user
      // gesture in Chromium/Firefox because the new navigation is considered
      // a continuation of the prior gesture in the same task.
      if (pendingFullscreen && !document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(() => {});
      }
      // If we are NOT going back into fullscreen, scroll the player back into
      // view so the user does not see the top of the page (default browser
      // behaviour after SPA navigation).
      if (!pendingFullscreen && !document.fullscreenElement && containerRef.current) {
        try {
          containerRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
        } catch (_) { /* old browsers */ }
      }
      // Auto-play after a short delay
      setTimeout(() => {
        v.play().catch(() => {});
        if (pendingFullscreen) setPendingFullscreen(false);
      }, 300);
    };

    // If video is already loaded enough, resume immediately
    if (v.readyState >= 1) {
      doResume();
    } else {
      // Wait for metadata to load
      const onLoaded = () => { doResume(); v.removeEventListener("loadedmetadata", onLoaded); };
      v.addEventListener("loadedmetadata", onLoaded);
      return () => v.removeEventListener("loadedmetadata", onLoaded);
    }
  }, [pendingResumeTime, hlsReady]);

    const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  }, []);

  const seek = useCallback((time) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = time;
    setCurrentTime(time);
  }, []);

  const setQuality = useCallback((levelIndex) => {
    if (!hlsRef.current) return;
    hlsRef.current.currentLevel = levelIndex;
    setCurrentQuality(levelIndex);
    setShowQualityMenu(false);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Lock page scroll while in fullscreen — avoids the chrome-less page being scrollable.
  useEffect(() => {
    if (!isFullscreen) return;
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
    };
  }, [isFullscreen]);

  // YouTube-style keyboard controls
  useEffect(() => {
    const handleKeyboard = (e) => {
      const v = videoRef.current;
      if (!v || !hlsReady) return;
      // Ignore if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;

      switch (e.key) {
        case ' ':  // Space - play/pause
        case 'k':
          e.preventDefault();
          togglePlay();
          break;
        case 'ArrowLeft':  // Left - rewind 5s
          e.preventDefault();
          seek(Math.max(0, v.currentTime - 5));
          break;
        case 'ArrowRight':  // Right - forward 5s
          e.preventDefault();
          seek(Math.min(v.duration || 0, v.currentTime + 5));
          break;
        case 'ArrowUp':  // Up - volume +5%
          e.preventDefault();
          v.volume = Math.min(1, v.volume + 0.05);
          v.muted = false;
          break;
        case 'ArrowDown':  // Down - volume -5%
          e.preventDefault();
          v.volume = Math.max(0, v.volume - 0.05);
          break;
        case 'j':  // j - rewind 10s
          e.preventDefault();
          seek(Math.max(0, v.currentTime - 10));
          break;
        case 'l':  // l - forward 10s
          e.preventDefault();
          seek(Math.min(v.duration || 0, v.currentTime + 10));
          break;
        case 'f':  // f - fullscreen
          e.preventDefault();
          toggleFullscreen();
          break;
        case 'm':  // m - mute/unmute
          e.preventDefault();
          v.muted = !v.muted;
          break;
        case 'Home':  // Home - go to start
          e.preventDefault();
          seek(0);
          break;
        case 'End':  // End - go to end
          e.preventDefault();
          seek(v.duration || 0);
          break;
        case '0': case '1': case '2': case '3': case '4':
        case '5': case '6': case '7': case '8': case '9':
          // Number keys - seek to 0%-90%
          e.preventDefault();
          seek((parseInt(e.key) / 10) * (v.duration || 0));
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyboard);
    return () => document.removeEventListener('keydown', handleKeyboard);
  }, [hlsReady, togglePlay, seek, toggleFullscreen]);

  // Auto-hide controls
  const scheduleHide = useCallback(() => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    setShowControls(true);
    hideTimer.current = setTimeout(() => { if (isPlaying) setShowControls(false); }, 3000);
  }, [isPlaying]);

  useEffect(() => {
    if (!isPlaying) setShowControls(true);
    else scheduleHide();
  }, [isPlaying, scheduleHide]);

  // Processing UI
  if (processing) {
    const info = processingInfo || {};
    return (
      <div style={{ ...style, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0f172a', color: '#fff', borderRadius: '12px', padding: '2rem', minHeight: 280 }}>
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem', position: 'relative' }}>
          <Loader2 className="animate-spin" size={28} style={{ color: '#fff' }} />
          {info.progress > 0 && info.progress < 100 && (
            <svg style={{ position: 'absolute', top: -3, left: -3, width: 62, height: 62, transform: 'rotate(-90deg)' }}>
              <circle cx="31" cy="31" r="28" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="3" />
              <circle cx="31" cy="31" r="28" fill="none" stroke="#a5b4fc" strokeWidth="3" strokeDasharray={`${2*Math.PI*28}`} strokeDashoffset={`${2*Math.PI*28*(1-info.progress/100)}`} strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.5s ease' }} />
            </svg>
          )}
        </div>
        <p style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.5rem', color: '#e0e7ff' }}>Обработка видео</p>
        <p style={{ fontSize: '0.95rem', marginBottom: '1rem', color: '#a5b4fc' }}>{info.step || 'Подготовка...'}</p>
        <div style={{ width: '85%', maxWidth: 450, height: 10, backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 5, overflow: 'hidden', marginBottom: '0.75rem' }}>
          <div style={{ width: `${info.progress||0}%`, height: '100%', background: 'linear-gradient(90deg, #6366f1, #a78bfa)', borderRadius: 5, transition: 'width 1s ease' }} />
        </div>
        <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.85rem', color: '#94a3b8' }}>
          <span>{info.progress||0}%</span>
          {info.elapsedSec > 0 && <span>Прошло: {Math.floor(info.elapsedSec/60)}:{String(info.elapsedSec%60).padStart(2,'0')}</span>}
          {info.etaSec > 0 && <span>Осталось: ~{Math.floor(info.etaSec/60)}:{String(info.etaSec%60).padStart(2,'0')}</span>}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
          {['360p','480p','720p','1080p'].map((q,i) => {
            const done = (info.progress||0) > ((i+1)/4)*100;
            const active = info.step && info.step.includes(q);
            return (<div key={q} style={{ padding: '0.25rem 0.75rem', borderRadius: 999, fontSize: '0.75rem', fontWeight: 500, background: done ? 'rgba(34,197,94,0.2)' : active ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.05)', color: done ? '#4ade80' : active ? '#a5b4fc' : '#64748b', border: `1px solid ${done ? 'rgba(34,197,94,0.3)' : active ? 'rgba(99,102,241,0.4)' : 'rgba(255,255,255,0.1)'}`, transition: 'all 0.3s ease' }}>{done ? '\u2713 ' : active ? '\u25C9 ' : '\u25CB '}{q}</div>);
          })}
        </div>
      </div>
    );
  }

  if (!hlsReady) return null;

  const currentQualityLabel = currentQuality === -1 ? 'Авто' : (qualities[currentQuality]?.height + 'p' || '?');

  return (
    <div
      ref={containerRef}
      style={{ ...style, ...(isFullscreen ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh', maxWidth: '100vw', maxHeight: '100vh', zIndex: 99998, borderRadius: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' } : { position: 'relative', borderRadius: '12px' }), backgroundColor: '#000', overflow: 'hidden', cursor: showControls ? 'default' : 'none' }}
      onMouseMove={scheduleHide}
      onMouseLeave={() => { if (isPlaying) setShowControls(false); }}
      onClick={(e) => {
        if (e.target === videoRef.current || e.target === containerRef.current) togglePlay();
      }}
    >
      <video
        ref={videoRef}
        style={ isFullscreen ? { width: '100%', height: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block', background: '#000' } : { width: '100%', maxHeight: 600, display: 'block' } }
        poster={poster}
        playsInline
      />

      {/* Controls overlay */}
      <div
        style={{
          position: 'absolute', left: 0, right: 0, bottom: 0,
          background: 'linear-gradient(transparent, rgba(0,0,0,0.85))',
          padding: '16px 16px 12px',
          opacity: showControls ? 1 : 0,
          transition: 'opacity 0.25s',
          pointerEvents: showControls ? 'auto' : 'none'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Progress bar */}
        <div
          style={{ height: 6, background: 'rgba(255,255,255,0.2)', borderRadius: 3, cursor: 'pointer', position: 'relative', marginBottom: 10 }}
          onMouseDown={(e) => {
            setIsSeeking(true);
            const bar = e.currentTarget;
            const update = (ev) => {
              const r = bar.getBoundingClientRect();
              const pct = Math.max(0, Math.min(1, (ev.clientX - r.left) / r.width));
              seek(pct * duration);
            };
            update(e);
            const onMove = (ev) => update(ev);
            const onUp = (ev) => {
              update(ev);
              setIsSeeking(false);
              document.removeEventListener('mousemove', onMove);
              document.removeEventListener('mouseup', onUp);
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
          }}
        >
          {/* Played */}
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${duration ? (currentTime/duration)*100 : 0}%`, background: '#fff', borderRadius: 3, transition: isSeeking ? 'none' : 'width 0.15s' }} />
          {/* Seek handle */}
          <div style={{ position: 'absolute', left: `${duration ? (currentTime/duration)*100 : 0}%`, top: '50%', transform: 'translate(-50%,-50%)', width: 14, height: 14, background: '#fff', borderRadius: '50%', boxShadow: '0 0 4px rgba(0,0,0,0.4)', opacity: showControls ? 1 : 0, transition: 'opacity 0.2s' }} />
          {/* Timecode markers */}
          {timecodes.map((tc, i) => (
            <div key={i}
              onClick={(e) => { e.stopPropagation(); seek(tc.timecode || tc.time); }}
              title={tc.panorama_name || `Таймкод ${fmt(tc.timecode || tc.time)}`}
              style={{ position: 'absolute', left: `${duration ? ((tc.timecode||tc.time)/duration)*100 : 0}%`, top: '50%', transform: 'translate(-50%,-50%)', width: 10, height: 10, background: '#f59e0b', borderRadius: '50%', cursor: 'pointer', zIndex: 2, border: '2px solid rgba(0,0,0,0.4)' }}
            />
          ))}
        </div>

        {/* Control buttons row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {/* Play/Pause */}
          <button onClick={togglePlay} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 0, display: 'flex' }}>
            {isPlaying
              ? <svg width={22} height={22} viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
              : <svg width={22} height={22} viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
            }
          </button>

          {/* Time */}
          <span style={{ color: '#fff', fontSize: 13, fontFamily: 'monospace', minWidth: 90 }}>{fmt(currentTime)} / {fmt(duration)}</span>

          {/* Spacer */}
          <div style={{ flex: 1 }} />

          {/* Volume */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}
            onMouseEnter={() => setShowVolumeSlider(true)}
            onMouseLeave={() => setShowVolumeSlider(false)}
          >
            <button onClick={() => { const v = videoRef.current; if(v) { v.muted = !v.muted; } }} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 0, display: 'flex' }}>
              <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {isMuted || volume === 0
                  ? <><path d="M11 5L6 9H2v6h4l5 4V5z"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></>
                  : volume < 0.5
                    ? <><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M15.54 8.46a5 5 0 010 7.07"/></>
                    : <><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M15.54 8.46a5 5 0 010 7.07"/><path d="M19.07 4.93a10 10 0 010 14.14"/></>
                }
              </svg>
            </button>
            {showVolumeSlider && (
              <input type="range" min="0" max="1" step="0.05" value={isMuted ? 0 : volume}
                onChange={(e) => { const v = videoRef.current; if(v) { v.volume = parseFloat(e.target.value); v.muted = false; } }}
                style={{ width: 80, marginLeft: 8, accentColor: '#fff', height: 4, cursor: 'pointer' }}
              />
            )}
          </div>

          {/* Quality picker */}
          {qualities.length >= 1 && (
            <div style={{ position: 'relative' }}>
              <button onClick={() => setShowQualityMenu(!showQualityMenu)} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', color: '#fff', cursor: 'pointer', padding: '3px 10px', borderRadius: 4, fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
                {currentQualityLabel}
              </button>
              {showQualityMenu && (
                <div style={{ position: 'absolute', bottom: '100%', right: 0, marginBottom: 6, background: 'rgba(15,23,42,0.95)', borderRadius: 8, padding: 4, minWidth: 120, boxShadow: '0 4px 20px rgba(0,0,0,0.5)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <div onClick={() => setQuality(-1)} style={{ padding: '8px 12px', cursor: 'pointer', borderRadius: 6, fontSize: 13, color: currentQuality === -1 ? '#a5b4fc' : '#fff', background: currentQuality === -1 ? 'rgba(99,102,241,0.2)' : 'transparent', fontWeight: currentQuality === -1 ? 600 : 400, display: 'flex', justifyContent: 'space-between' }}>
                    <span>Авто</span>
                    {currentQuality === -1 && <span style={{ color: '#818cf8' }}>&#10003;</span>}
                  </div>
                  {[...qualities].sort((a,b) => b.height - a.height).map((q) => (
                    <div key={q.index} onClick={() => setQuality(q.index)} style={{ padding: '8px 12px', cursor: 'pointer', borderRadius: 6, fontSize: 13, color: currentQuality === q.index ? '#a5b4fc' : '#fff', background: currentQuality === q.index ? 'rgba(99,102,241,0.2)' : 'transparent', fontWeight: currentQuality === q.index ? 600 : 400, display: 'flex', justifyContent: 'space-between' }}>
                      <span>{q.height}p</span>
                      {currentQuality === q.index && <span style={{ color: '#818cf8' }}>&#10003;</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Fullscreen */}
          <button onClick={toggleFullscreen} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 0, display: 'flex' }}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {isFullscreen
                ? <path d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3"/>
                : <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
              }
            </svg>
          </button>
        </div>
      </div>

      {/* Flash effect */}
      {showFlash && (
        <div style={{ position: 'absolute', inset: 0, background: '#fff', zIndex: 50, animation: 'flashFade 0.15s ease-out forwards' }} />
      )}

      {/* Countdown overlay */}
      {countdown > 0 && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', zIndex: 40 }}>
          <span style={{ fontSize: 72, fontWeight: 700, color: '#fff', textShadow: '0 2px 20px rgba(0,0,0,0.5)' }}>{countdown}</span>
        </div>
      )}

      {/* Big play button overlay - hidden in coverOnly mode */}
      {!coverOnly && !isPlaying && currentTime === 0 && (
        <div onClick={togglePlay} style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 64, height: 64, borderRadius: '50%', background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', backdropFilter: 'blur(4px)', transition: 'transform 0.2s' }}>
          <svg width={32} height={32} viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z"/></svg>
        </div>
      )}
    </div>
  );
};

export default InteractiveGamePlayer;
